import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ArrowLeft, Trash2, Search, Users, Eye, Ban, CheckCircle, KeyRound } from 'lucide-react';

const API = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';
const BACKEND_URL = typeof window !== 'undefined' ? window.location.origin : '';

const getImageUrl = (image) => {
  if (!image) return null;
  if (image.startsWith('data:image')) return image;
  if (image.startsWith('http')) return image;
  if (image.startsWith('/uploads')) return `${BACKEND_URL}/api${image}`;
  return `data:image/jpeg;base64,${image}`;
};

const AdminUsers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [stats, setStats] = useState({ total: 0, free: 0, creators: 0, viewers: 0 });

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchUsers();
    }
  }, [user]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/admin/users/list`, { headers });
      setUsers(response.data.users || []);
      setStats(response.data.stats || { total: 0, free: 0, creators: 0, viewers: 0 });
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/users/${selectedUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuário excluído com sucesso!');
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/admin/users/${userId}/status`, 
        { active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(currentStatus ? 'Usuário desativado' : 'Usuário ativado');
      fetchUsers();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast.error('Erro ao alterar status do usuário');
    }
  };

  const resetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error('A senha deve ter no mínimo 6 caracteres');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users/${selectedUser.id}/reset-password`, 
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Senha redefinida com sucesso!');
      setShowResetPasswordModal(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      console.error('Erro ao redefinir senha:', error);
      toast.error(error.response?.data?.detail || 'Erro ao redefinir senha');
    }
  };

  const getRoleBadge = (role, isFundador) => {
    if (isFundador) return <Badge className="bg-yellow-500/20 text-yellow-400">Fundador</Badge>;
    switch (role) {
      case 'admin': return <Badge className="bg-red-500/20 text-red-400">Admin</Badge>;
      case 'creator': return <Badge className="bg-purple-500/20 text-purple-400">Criador</Badge>;
      case 'free': return <Badge className="bg-blue-500/20 text-blue-400">Free</Badge>;
      case 'viewer': return <Badge className="bg-gray-500/20 text-gray-400">Viewer</Badge>;
      default: return <Badge className="bg-gray-500/20 text-gray-400">{role}</Badge>;
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = !searchTerm || 
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.username?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#C79F5E] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white pb-10">
      <div className="max-w-7xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/dashboard')}
            className="border-[#C79F5E]/30 text-[#C79F5E] hover:bg-[#C79F5E]/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#C79F5E]">👤 Gerenciar Usuários</h1>
            <p className="text-gray-400 text-sm">Visualize, analise e gerencie todos os usuários</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#1A1A1A] border-[#C79F5E]/20">
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 text-[#C79F5E] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats.total}</p>
              <p className="text-sm text-gray-400">Total</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1A1A1A] border-blue-500/20">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-400">{stats.free}</p>
              <p className="text-sm text-gray-400">Free (Casal/Solteira)</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1A1A1A] border-purple-500/20">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-400">{stats.creators}</p>
              <p className="text-sm text-gray-400">Criadores</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1A1A1A] border-gray-500/20">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-gray-400">{stats.viewers}</p>
              <p className="text-sm text-gray-400">Viewers</p>
            </CardContent>
          </Card>
        </div>

        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              placeholder="Buscar por nome, email ou username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#1A1A1A] border border-[#C79F5E]/20 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#C79F5E]"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="bg-[#1A1A1A] border border-[#C79F5E]/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#C79F5E]"
          >
            <option value="all">Todos os tipos</option>
            <option value="free">Free (Casal/Solteira)</option>
            <option value="creator">Criadores</option>
            <option value="viewer">Viewers</option>
            <option value="admin">Admins</option>
          </select>
        </div>

        {/* Lista de Usuários */}
        <Card className="bg-[#1A1A1A] border-[#C79F5E]/20">
          <CardHeader>
            <CardTitle className="text-[#C79F5E]">
              {filteredUsers.length} usuário(s) encontrado(s)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredUsers.length === 0 ? (
              <p className="text-gray-400 text-center py-8">Nenhum usuário encontrado</p>
            ) : (
              <div className="space-y-3">
                {filteredUsers.map((u) => (
                  <div key={u.id} className="flex items-center gap-4 p-4 bg-[#0D0D0D]/50 rounded-lg hover:bg-[#0D0D0D] transition-colors">
                    {/* Avatar */}
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-700 flex-shrink-0">
                      {u.picture ? (
                        <img src={getImageUrl(u.picture)} alt={u.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#4D1A4A] to-[#6B2D5C] flex items-center justify-center text-white font-bold">
                          {(u.name || 'U').charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-white font-semibold truncate">{u.name || 'Sem nome'}</p>
                        {getRoleBadge(u.role, u.is_fundador)}
                        {u.is_godparent && <Badge className="bg-pink-500/20 text-pink-400">Padrinho</Badge>}
                        {u.active === false && <Badge className="bg-red-500/20 text-red-400">Inativo</Badge>}
                      </div>
                      <p className="text-sm text-gray-400 truncate">{u.email}</p>
                      <p className="text-xs text-gray-500">
                        @{u.username || 'sem-username'} • Cadastro: {formatDate(u.created_at)}
                      </p>
                    </div>

                    {/* Relationship Status */}
                    <div className="hidden md:block text-center px-4">
                      <p className="text-sm text-gray-300">{u.relationship_status || '-'}</p>
                      <p className="text-xs text-gray-500">Status</p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/creator/${u.id}`)}
                        className="border-[#C79F5E]/30 text-[#C79F5E]"
                        title="Ver perfil"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedUser(u);
                          setNewPassword('');
                          setShowResetPasswordModal(true);
                        }}
                        className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                        title="Redefinir Senha"
                      >
                        <KeyRound className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleUserStatus(u.id, u.active !== false)}
                        className={u.active === false ? "border-green-500/30 text-green-400" : "border-yellow-500/30 text-yellow-400"}
                        title={u.active === false ? "Ativar" : "Desativar"}
                      >
                        {u.active === false ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedUser(u);
                          setShowDeleteModal(true);
                        }}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal de Confirmação de Exclusão */}
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent className="bg-[#1A1A1A] border-red-500/30">
            <DialogHeader>
              <DialogTitle className="text-red-400">⚠️ Confirmar Exclusão</DialogTitle>
              <DialogDescription className="text-gray-400">
                Tem certeza que deseja excluir o usuário <strong className="text-white">{selectedUser?.name}</strong>?
                <br />
                <span className="text-red-400">Esta ação não pode ser desfeita.</span>
              </DialogDescription>
            </DialogHeader>
            
            {selectedUser && (
              <div className="bg-[#0D0D0D] rounded-lg p-4 my-4">
                <p className="text-sm text-gray-400">Email: <span className="text-white">{selectedUser.email}</span></p>
                <p className="text-sm text-gray-400">Tipo: <span className="text-white">{selectedUser.role}</span></p>
              </div>
            )}
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 border-[#C79F5E]/30"
              >
                Cancelar
              </Button>
              <Button
                onClick={deleteUser}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Excluir Usuário
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal de Redefinição de Senha */}
        <Dialog open={showResetPasswordModal} onOpenChange={setShowResetPasswordModal}>
          <DialogContent className="bg-[#1A1A1A] border-blue-500/30">
            <DialogHeader>
              <DialogTitle className="text-blue-400">🔑 Redefinir Senha</DialogTitle>
              <DialogDescription className="text-gray-400">
                Defina uma nova senha para o usuário <strong className="text-white">{selectedUser?.name}</strong>
              </DialogDescription>
            </DialogHeader>
            
            {selectedUser && (
              <div className="bg-[#0D0D0D] rounded-lg p-4 my-4">
                <p className="text-sm text-gray-400">Email: <span className="text-white">{selectedUser.email}</span></p>
                <p className="text-sm text-gray-400">Username: <span className="text-white">@{selectedUser.username || 'sem-username'}</span></p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 block mb-2">Nova Senha</label>
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Digite a nova senha (mín. 6 caracteres)"
                  className="w-full bg-[#0D0D0D] border border-[#C79F5E]/20 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowResetPasswordModal(false);
                  setNewPassword('');
                }}
                className="flex-1 border-[#C79F5E]/30"
              >
                Cancelar
              </Button>
              <Button
                onClick={resetPassword}
                disabled={!newPassword || newPassword.length < 6}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
              >
                Redefinir Senha
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminUsers;
